import * as Handlebars from "handlebars";
import dayjs from "dayjs";

// ── Date helpers ────────────────────────────────────────────────────────────

// Named presets so callers don't need to remember dayjs tokens
const PRESETS: Record<string, string> = {
  long: "dddd, MMMM D, YYYY", // Sunday, June 15, 2026
  short: "MMM D, YYYY", // Jun 15, 2026
  numeric: "MM/DD/YYYY", // 06/15/2026
  iso: "YYYY-MM-DD", // 2026-06-15
  time: "h:mm A", // 3:45 PM
  time24: "HH:mm", // 15:45
  datetime: "MMMM D, YYYY h:mm A", // June 15, 2026 3:45 PM
  month: "MMMM YYYY", // June 2026
  monthShort: "MMM YYYY", // Jun 2026
  dayMonth: "MMMM D", // June 15
};

const formatDate = (date: Date, fmt?: string): string => {
  const resolved = PRESETS[fmt ?? "long"] ?? fmt ?? PRESETS.long;
  return dayjs(date).format(resolved);
};

/**
 * Accepts named presets or any dayjs format string.
 *
 * Presets:  long · short · numeric · iso · time · time24 · datetime · month · monthShort · dayMonth
 * Tokens:   any dayjs format string, e.g. "Do [of] MMMM" → "15th of June"
 *
 * {{currentDate}}                   → "Sunday, June 15, 2026"
 * {{currentDate "short"}}           → "Jun 15, 2026"
 * {{currentDate "iso"}}             → "2026-06-15"
 * {{currentDate "time"}}            → "3:45 PM"
 * {{currentDate "Do [of] MMMM"}}   → "15th of June"
 */
Handlebars.registerHelper("currentDate", (format?: string) => {
  return formatDate(new Date(), typeof format === "string" ? format : undefined);
});

/**
 * {{nextMonth}}                            → "Wednesday, July 15, 2026"
 * {{nextMonth "short"}}                    → "Jul 15, 2026"
 * {{nextMonth "iso"}}                      → "2026-07-15"
 * {{nextMonth "month"}}                    → "July 2026"
 *
 * Advances by one calendar month, clamping to the last day of the target month
 * (e.g. Jan 31 → Feb 28).
 */
Handlebars.registerHelper("nextMonth", (format?: string) => {
  // dayjs clamps month-end naturally (e.g. Jan 31 → Feb 28)
  const target = dayjs().add(1, "month").toDate();
  return formatDate(target, typeof format === "string" ? format : undefined);
});

/**
 * {{dateOffset -1}}                              → "Saturday, June 14, 2026"
 * {{dateOffset 1}}                               → "Monday, June 16, 2026"
 * {{dateOffset -1 "relative"}}                   → "yesterday"
 * {{dateOffset 0 "relative"}}                    → "today"
 * {{dateOffset 1 "relative"}}                    → "tomorrow"
 * {{dateOffset -7 "relative"}}                   → "7 days ago"
 * {{dateOffset 7 "relative"}}                    → "7 days from now"
 * {{dateOffset 7 "short"}}                       → "Jun 22, 2026"
 * {{dateOffset 7 "Do [of] MMMM"}}               → "22nd of June"
 * {{dateOffset -30 "iso"}}                       → "2026-05-16"
 * {{dateOffset 7 from=orderDate}}                → "Sunday, June 22, 2026" (7 days after orderDate)
 * {{dateOffset 7 "short" from=orderDate}}        → "Jun 22, 2026" (7 days after orderDate)
 *
 * `from` accepts a Date object, ISO string, or timestamp number.
 */
Handlebars.registerHelper(
  "dateOffset",
  (days: number, format?: string | Handlebars.HelperOptions, options?: Handlebars.HelperOptions) => {
    const fmt = typeof format === "string" ? format : undefined;
    const opts = typeof format === "string" ? options : (format as Handlebars.HelperOptions);
    const fromVal = opts?.hash?.from;
    const baseDate = fromVal ? new Date(fromVal as string | number) : new Date();
    const d = dayjs(baseDate).add(days, "day").toDate();

    if (fmt === "relative") {
      if (days === -1) return "yesterday";
      if (days === 0) return "today";
      if (days === 1) return "tomorrow";
      return days < 0 ? `${Math.abs(days)} days ago` : `${days} days from now`;
    }

    return formatDate(d, fmt);
  },
);

/**
 * Format a date value from context using any preset or date-fns format string.
 * Accepts a Date object, ISO string, or timestamp number.
 *
 * {{formatDate orderDate "short"}}           → "Jun 15, 2026"
 * {{formatDate orderDate "iso"}}             → "2026-06-15"
 * {{formatDate orderDate "Do [of] MMMM"}}   → "15th of June"
 * {{formatDate orderDate}}                   → "Sunday, June 15, 2026"
 */
Handlebars.registerHelper("formatDate", (value: unknown, format?: string) => {
  const date = value instanceof Date ? value : new Date(value as string | number);
  if (isNaN(date.getTime())) return "";
  return formatDate(date, typeof format === "string" ? format : undefined);
});

// ── Color helper ─────────────────────────────────────────────────────────────

/**
 * {{color "red" "Sale ends today!"}}
 * {{color "#ff6600" someVar}}
 *
 * Accepts any valid CSS color value (named color, hex, rgb(), hsl(), etc.).
 */
Handlebars.registerHelper("color", (colorValue: string, text: unknown) => {
  const escaped = Handlebars.escapeExpression(text as any);
  const safeColor = Handlebars.escapeExpression(colorValue);
  return new Handlebars.SafeString(`<span style="color:${safeColor}">${escaped}</span>`);
});

// ── Layout helpers ───────────────────────────────────────────────────────────

/** {{br}} → inserts a line break */
Handlebars.registerHelper("br", () => new Handlebars.SafeString("<br>"));

// ── Font-style helpers ────────────────────────────────────────────────────────

/** {{bold "Important!"}} */
Handlebars.registerHelper("bold", (text: unknown) => {
  const escaped = Handlebars.escapeExpression(text as any);
  return new Handlebars.SafeString(`<strong>${escaped}</strong>`);
});

/** {{italic "Note:"}} */
Handlebars.registerHelper("italic", (text: unknown) => {
  const escaped = Handlebars.escapeExpression(text as any);
  return new Handlebars.SafeString(`<em>${escaped}</em>`);
});

/** {{underline "Click here"}} */
Handlebars.registerHelper("underline", (text: unknown) => {
  const escaped = Handlebars.escapeExpression(text as any);
  return new Handlebars.SafeString(`<span style="text-decoration:underline">${escaped}</span>`);
});

/** {{strikethrough "Was $99"}} */
Handlebars.registerHelper("strikethrough", (text: unknown) => {
  const escaped = Handlebars.escapeExpression(text as any);
  return new Handlebars.SafeString(`<s>${escaped}</s>`);
});

/** {{uppercase "hello"}} → renders as "HELLO" via CSS so the source text is preserved */
Handlebars.registerHelper("uppercase", (text: unknown) => {
  const escaped = Handlebars.escapeExpression(text as any);
  return new Handlebars.SafeString(`<span style="text-transform:uppercase">${escaped}</span>`);
});

/** {{lowercase "HELLO"}} */
Handlebars.registerHelper("lowercase", (text: unknown) => {
  const escaped = Handlebars.escapeExpression(text as any);
  return new Handlebars.SafeString(`<span style="text-transform:lowercase">${escaped}</span>`);
});

/**
 * {{capitalize "hello world"}}  → "Hello world"  (first letter only — use uppercase for ALL CAPS)
 * Note: CSS text-transform:capitalize uppercases every word; this helper only touches the first character.
 */
Handlebars.registerHelper("capitalize", (text: unknown) => {
  const str = String(text ?? "");
  const capitalized = str.charAt(0).toUpperCase() + str.slice(1);
  return new Handlebars.SafeString(Handlebars.escapeExpression(capitalized));
});

/**
 * Combine multiple styles at once.
 *
 * {{style text bold=true italic=true color="#333" underline=true strikethrough=false fontSize="16px" fontFamily="Georgia, serif"}}
 */
Handlebars.registerHelper(
  "style",
  (
    text: unknown,
    options: Handlebars.HelperOptions & {
      hash: {
        bold?: boolean;
        italic?: boolean;
        underline?: boolean;
        strikethrough?: boolean;
        uppercase?: boolean;
        lowercase?: boolean;
        color?: string;
        fontSize?: string;
        fontFamily?: string;
      };
    },
  ) => {
    const { bold, italic, underline, strikethrough, uppercase, lowercase, color, fontSize, fontFamily } = options.hash;

    let content = Handlebars.escapeExpression(text as any);

    if (bold) content = `<strong>${content}</strong>`;
    if (italic) content = `<em>${content}</em>`;
    if (strikethrough) content = `<s>${content}</s>`;

    const cssProps: string[] = [];
    if (underline) cssProps.push("text-decoration:underline");
    if (uppercase) cssProps.push("text-transform:uppercase");
    if (lowercase) cssProps.push("text-transform:lowercase");
    if (color) cssProps.push(`color:${Handlebars.escapeExpression(color)}`);
    if (fontSize) cssProps.push(`font-size:${Handlebars.escapeExpression(fontSize)}`);
    if (fontFamily) cssProps.push(`font-family:${Handlebars.escapeExpression(fontFamily)}`);

    if (cssProps.length) {
      content = `<span style="${cssProps.join(";")}">${content}</span>`;
    }

    return new Handlebars.SafeString(content);
  },
);

export { Handlebars };
